import {
  FolderOpen,
  Target,
  CheckCircle2,
  FileText,
  Clock,
  ArrowRightCircle,
} from "lucide-react";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TimelineEvent {
  id: string;
  date: string;
  type:
    | "case_created"
    | "goal_added"
    | "goal_completed"
    | "goal_updated"
    | "note_added"
    | "status_change";
  title: string;
  description?: string;
  meta?: Record<string, string>;
}

interface CaseTimelineProps {
  events: TimelineEvent[];
  isLoading?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const typeConfig: Record<
  TimelineEvent["type"],
  { icon: typeof FolderOpen; color: string; bgColor: string }
> = {
  case_created: {
    icon: FolderOpen,
    color: "text-blue-600",
    bgColor: "bg-blue-100",
  },
  goal_added: {
    icon: Target,
    color: "text-purple-600",
    bgColor: "bg-purple-100",
  },
  goal_completed: {
    icon: CheckCircle2,
    color: "text-green-600",
    bgColor: "bg-green-100",
  },
  goal_updated: {
    icon: ArrowRightCircle,
    color: "text-amber-600",
    bgColor: "bg-amber-100",
  },
  note_added: {
    icon: FileText,
    color: "text-gray-600",
    bgColor: "bg-gray-100",
  },
  status_change: {
    icon: Clock,
    color: "text-indigo-600",
    bgColor: "bg-indigo-100",
  },
};

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CaseTimeline({ events, isLoading }: CaseTimelineProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-4">
            <div className="h-8 w-8 animate-pulse rounded-full bg-muted" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-48 animate-pulse rounded bg-muted" />
              <div className="h-3 w-32 animate-pulse rounded bg-muted" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="py-8 text-center">
        <Clock className="mx-auto h-10 w-10 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">
          No timeline events yet.
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Vertical line */}
      <div className="absolute left-4 top-0 h-full w-0.5 bg-border" />

      <div className="space-y-6">
        {events.map((event) => {
          const config = typeConfig[event.type];
          const Icon = config.icon;

          return (
            <div key={event.id} className="relative flex gap-4 pl-0">
              {/* Icon dot */}
              <div
                className={cn(
                  "relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                  config.bgColor,
                )}
              >
                <Icon className={cn("h-4 w-4", config.color)} />
              </div>

              {/* Content */}
              <div className="flex-1 pb-1 pt-0.5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {event.title}
                    </p>
                    {event.description && (
                      <p className="mt-0.5 text-sm text-muted-foreground">
                        {event.description}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-xs text-muted-foreground">
                      {formatDate(event.date)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatTime(event.date)}
                    </p>
                  </div>
                </div>

                {/* Meta tags */}
                {event.meta && Object.keys(event.meta).length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {Object.entries(event.meta).map(([key, value]) => (
                      <span
                        key={key}
                        className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                      >
                        {key}: {value}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helper to build timeline events from case data
// ---------------------------------------------------------------------------

export function buildTimelineEvents(caseData: {
  created_at: string;
  status: string | null;
  description: string | null;
  goals?: Array<{
    id: number;
    goal_text: string;
    status: string | null;
    completed_date: string | null;
  }>;
  notes?: Array<{
    id: number;
    note_date: string;
    note_text: string;
    author_first_name?: string;
    author_last_name?: string;
  }>;
}): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  // Case created
  events.push({
    id: "case-created",
    date: caseData.created_at,
    type: "case_created",
    title: "Case created",
    description: caseData.description ?? undefined,
  });

  // Goals
  if (caseData.goals) {
    for (const goal of caseData.goals) {
      events.push({
        id: `goal-added-${goal.id}`,
        date: caseData.created_at, // use case date as proxy
        type: "goal_added",
        title: "Goal added",
        description: goal.goal_text,
        meta: { status: (goal.status ?? "pending").replace("_", " ") },
      });

      if (goal.completed_date) {
        events.push({
          id: `goal-completed-${goal.id}`,
          date: goal.completed_date,
          type: "goal_completed",
          title: "Goal completed",
          description: goal.goal_text,
        });
      }
    }
  }

  // Notes
  if (caseData.notes) {
    for (const note of caseData.notes) {
      const author =
        note.author_first_name && note.author_last_name
          ? `${note.author_first_name} ${note.author_last_name}`
          : undefined;
      events.push({
        id: `note-${note.id}`,
        date: note.note_date,
        type: "note_added",
        title: "Note added",
        description:
          note.note_text.length > 120
            ? note.note_text.slice(0, 120) + "..."
            : note.note_text,
        meta: author ? { by: author } : undefined,
      });
    }
  }

  // Sort by date descending
  events.sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  return events;
}
